import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Box,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import {
  GameState,
  GAME_LIST_PLATFORMS,
  GAME_STATES,
  STATE_LABELS,
} from '../types';

export interface NewGameListEntry {
  title: string;
  year: number;
  image: string;
  platform: string;
  state: GameState;
}

interface AddGameListModalProps {
  show: boolean;
  onHide: () => void;
  onAddGame: (game: NewGameListEntry) => void;
}

function AddGameListModal({ show, onHide, onAddGame }: AddGameListModalProps) {
  const rawgApiKey = process.env.REACT_APP_RAWG_API_KEY;

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedGame, setSelectedGame] = useState<any | null>(null);
  const [platform, setPlatform] = useState('PS5');
  const [state, setState] = useState<GameState>('inProgress');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedGame(null);
      setPlatform('PS5');
      setState('inProgress');
      setError(null);
      setLoading(false);
    }
  }, [show]);

  const performSearch = useCallback(
    async (value: string) => {
      const trimmedValue = value.trim();

      if (trimmedValue.length < 2) {
        setSearchResults([]);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get('https://api.rawg.io/api/games', {
          params: {
            key: rawgApiKey,
            search: trimmedValue,
            page_size: 12,
          },
        });

        const mappedGames = (response.data.results || []).map((game: any) => ({
          id: game.id,
          title: game.name,
          year: game.released || '',
          image: game.background_image || '',
        }));

        setSearchResults(mappedGames);
      } catch (err: any) {
        console.error(err);
        const status = err?.response?.status;
        setError(
          status
            ? `Failed to fetch game data (HTTP ${status}).`
            : 'Could not reach RAWG — the request was blocked before it left the browser (ad blocker or offline?).'
        );
      } finally {
        setLoading(false);
      }
    },
    [rawgApiKey]
  );

  useEffect(() => {
    if (!show || selectedGame) return;

    const timeoutId = window.setTimeout(() => {
      performSearch(searchTerm);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [show, searchTerm, selectedGame, performSearch]);

  const handleAddGame = useCallback(() => {
    if (!selectedGame) return;

    onAddGame({
      title: selectedGame.title,
      year: selectedGame.year
        ? parseInt(String(selectedGame.year).split('-')[0], 10)
        : 0,
      image: selectedGame.image || '',
      platform,
      state,
    });

    onHide();
  }, [selectedGame, onAddGame, platform, state, onHide]);

  return (
    <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth>
      <DialogTitle>Add Game</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Search for a game"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Start typing a game title..."
            helperText={
              selectedGame
                ? 'Game selected below.'
                : searchTerm.trim().length < 2
                  ? 'Type at least 2 characters to search automatically.'
                  : 'Searching automatically as you type.'
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {selectedGame ? (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Selected Game
            </Typography>

            <Box
              component="img"
              src={selectedGame.image}
              alt={selectedGame.title}
              sx={{
                width: 220,
                maxWidth: '100%',
                borderRadius: 1,
                mb: 2,
                objectFit: 'cover',
              }}
            />

            <Typography sx={{ mb: 2 }}>{selectedGame.title}</Typography>

            <Button
              variant="outlined"
              sx={{ mb: 3 }}
              onClick={() => {
                setSelectedGame(null);
                setSearchResults([]);
              }}
            >
              Change Game
            </Button>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Platform</InputLabel>
              <Select
                value={platform}
                label="Platform"
                onChange={(e) => setPlatform(e.target.value)}
              >
                {GAME_LIST_PLATFORMS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>State</InputLabel>
              <Select
                value={state}
                label="State"
                onChange={(e) => setState(e.target.value as GameState)}
              >
                {GAME_STATES.map((option) => (
                  <MenuItem key={option} value={option}>
                    {STATE_LABELS[option]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {state === 'finished' && (
              <Typography variant="caption" color="text.secondary">
                Added as already completed — no completion date is recorded.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {!loading && searchTerm.trim().length >= 2 && searchResults.length === 0 && !error && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                No results found.
              </Typography>
            )}

            {searchResults.length > 0 && (
              <Grid
                container
                spacing={2}
                sx={{ maxHeight: 400, overflowY: 'auto', mt: 2 }}
              >
                {searchResults.map((game) => (
                  <Grid key={game.id} size={{ xs: 6, sm: 4, md: 3 }}>
                    <Box
                      component="img"
                      src={game.image}
                      alt={game.title}
                      onClick={() => setSelectedGame(game)}
                      sx={{
                        width: '100%',
                        aspectRatio: '3 / 4',
                        borderRadius: 1,
                        cursor: 'pointer',
                        objectFit: 'cover',
                        '&:hover': { opacity: 0.8 },
                      }}
                    />
                    <Typography align="center" sx={{ mt: 1 }}>
                      {game.title}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onHide}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAddGame}
          disabled={!selectedGame}
        >
          Add Game
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(AddGameListModal);
